import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule, MatFormFieldAppearance } from '@angular/material/form-field';
import { FormsModule } from '@angular/forms';
import { Vessel } from '../../dto/vessel';
import { StateNotifyService } from '../../../../common/state/service/state-notify.service';
import { VesselService } from '../../service/vessel.service';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../../../../common/component/dialog/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'dpi-vessel-details',
  imports: [FormsModule, MatFormFieldModule, MatInputModule, MatCardModule, MatIconModule, MatButtonModule, MatToolbarModule,
    MatTooltipModule, NgIf],
  templateUrl: './vessel-details.component.html',
  styleUrl: './vessel-details.component.scss'
})
export class VesselDetailsComponent implements OnInit, OnDestroy {
  vessel: Vessel;
  isReadonly: boolean;
  fieldAppearance: MatFormFieldAppearance;
  destroyed$ = new Subject<void>(); // notifier of component destroyed
  isContentChanged: boolean;
  private _dialogConfirm = inject(MatDialog);

  constructor(private notifyService: StateNotifyService, private vesselService: VesselService, private router: Router) {
    this.vessel = this.notifyService.getSelectedVessel();
    this.isReadonly = true;
    this.fieldAppearance = 'outline';
    this.isContentChanged = false;
  }

  ngOnInit(): void {
    this.notifyService.vesselSelection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(model => {
        console.log("Vessel-details: " + this.vessel.name + " -- " + model.name);
        this.reload(model.id);
      });

    this.notifyService.readonlyMode$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(res => {
        this.onEditModeChange(res);
      });
  }

  reload(vesselId: number): void {
    this.vesselService.getVesselById(vesselId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: res => {
          this.vessel = res;
        },
        error: err => { console.log(err); }
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onEditModeChange(isReadonly: boolean) {
    this.isReadonly = isReadonly;
    if (isReadonly) {
      this.fieldAppearance = 'outline';
    } else {
      this.fieldAppearance = 'fill';
    }
  }

  onContentChange() {
    this.isContentChanged = true;
  }

  // --- CRUD actions ---

  createVessel() {
    this.vesselService.createVessel(this.vessel.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: model => {
          this.addModelToSytem(model);
        },
        error: err => { console.log(err); }
      });
  }

  updateVessel() {
    this.isContentChanged = false; // Reset the state, and disable the button to prevent multiple clicks
    this.vesselService.updateVessel(this.vessel)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: model => {
          this.vessel = model;
          this.notifyService.updateVesselSelection(model);
        },
        error: err => { console.log(err); }
      });
  }

  deleteVessel() {
    this.vesselService.deleteVessel(this.vessel.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: res => {
          this.removeVesselAfterDelete();
        },
        error: err => { console.log(err); }
      });
  }

  private addModelToSytem(model: Vessel) {
    this.vessel = model;
    this.notifyService.updateVesselSelection(this.vessel);
    let listVessel = this.notifyService.getListAllVessels();
    listVessel.push(model);
    this.notifyService.updateListAllVessels(listVessel);
  }

  private removeVesselAfterDelete() {
    let listVessel = this.notifyService.getListAllVessels();
    const index = listVessel.indexOf(this.vessel, 0);
    if (index > -1) {
      listVessel.splice(index, 1);
    }
    this.notifyService.updateListAllVessels(listVessel);

    // Select the current 'customer', then navigate to 'customer-details', i.e. to load and display the list of all vessels after deletion.
    let customerModel = this.notifyService.getSelectedCustomer();
    this.notifyService.updateCustomerSelection(customerModel); //update the same customer selection, so that adaption of display features in 'app.component.ts' can be triggered. 
    this.router.navigate(['customer-details']);
  }

  btnDeleteClicked() {
    if (!this.isReadonly) {
      let message = "The whole vessel will be deleted, sure to proceed?"
      const dialogRef = this._dialogConfirm.open(ConfirmDialogComponent, { data: message });
      dialogRef.afterClosed()
        .pipe(takeUntil(this.destroyed$))
        .subscribe(result => {
          if (result !== undefined) {
            if (dialogRef.componentInstance.confirmed) {
              this.deleteVessel();
            }
          };
        });
    }
  }



}


